import React, { createContext, useContext, useMemo, useState } from "react";
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

export const buildSarasDnDId = (groupKey, rowKey) => `${String(groupKey)}::${String(rowKey)}`;

const SarasDnDDragContext = createContext({
    activeId: null,
    activeGroupKey: null,
    destinationGroupKey: null,
    destinationIndex: null,
    overId: null,
});

export const useSarasDnDDragState = () => {
    return useContext(SarasDnDDragContext);
}

const getDragMeta = (event) => {
    const { active, over } = event || {};

    if (!active) {
        return {
            activeData: {},
            activeGroupKey: null,
            destinationGroupKey: null,
            destinationIndex: null,
            overData: {},
        };
    }

    const activeData = active.data.current || {};
    const activeSortable = activeData.sortable || {};
    const overData = over?.data.current || {};
    const overSortable = overData.sortable || {};

    const activeGroupKey = activeSortable.containerId ?? activeData.groupKey ?? null;
    const destinationGroupKey = overSortable.containerId ?? overData.groupKey ?? null;
    const destinationIndex = overSortable.index ?? overData.itemCount ?? null;

    return {
        activeData,
        activeGroupKey,
        destinationGroupKey,
        destinationIndex,
        overData,
    };
}

const toLegacyDnDResult = (event) => {
    const { active, over } = event;

    if (!active) return null;

    const { activeData, activeGroupKey, destinationGroupKey, destinationIndex } = getDragMeta(event);
    const activeSortable = activeData.sortable || {};
    const sourceIndex = activeSortable.index;
    const sourceGroupKey = activeGroupKey;

    if (sourceIndex === undefined || sourceGroupKey === undefined) return null;

    return {
        draggableId: String(active.id),
        type: activeData.dndType,
        source: {
            droppableId: String(sourceGroupKey),
            index: sourceIndex,
        },
        destination: over && destinationIndex !== undefined && destinationGroupKey !== undefined ? {
            droppableId: String(destinationGroupKey),
            index: destinationIndex,
        } : null,
    };
}

const SarasDnDProvider = (props) => {
    const { children, onDragEnd, autoScroll = true, ...restProps } = props;
    const [activeItem, setActiveItem] = useState(null);
    const [dragState, setDragState] = useState({
        activeId: null,
        activeGroupKey: null,
        destinationGroupKey: null,
        destinationIndex: null,
        overId: null,
    });
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (event) => {
        // console.log('dndkit drag start');

        const { active } = event;
        const activeData = active?.data.current || {};
        const { activeGroupKey } = getDragMeta(event);

        setActiveItem({
            children: activeData.children || null,
            cardStyle: activeData.cardStyle || {},
        });

        setDragState({
            activeId: active ? String(active.id) : null,
            activeGroupKey: activeGroupKey ? String(activeGroupKey) : null,
            destinationGroupKey: activeGroupKey ? String(activeGroupKey) : null,
            destinationIndex: activeData.index ?? null,
            overId: null,
        });
    }

    const handleDragCancel = () => {
        setActiveItem(null);
        setDragState({
            activeId: null,
            activeGroupKey: null,
            destinationGroupKey: null,
            destinationIndex: null,
            overId: null,
        });
    }

    const handleDragOver = (event) => {
        const { over } = event;
        const { activeGroupKey, destinationGroupKey, destinationIndex } = getDragMeta(event);

        setDragState((currentState) => ({
            ...currentState,
            activeGroupKey: activeGroupKey ? String(activeGroupKey) : currentState.activeGroupKey,
            destinationGroupKey: destinationGroupKey ? String(destinationGroupKey) : null,
            destinationIndex: destinationIndex ?? null,
            overId: over ? String(over.id) : null,
        }));
    }

    const handleDragEnd = (event) => {
        const result = toLegacyDnDResult(event);

        setActiveItem(null);
        setDragState({
            activeId: null,
            activeGroupKey: null,
            destinationGroupKey: null,
            destinationIndex: null,
            overId: null,
        });

        if (onDragEnd) {
            onDragEnd(result);
        }
    }

    const dragContextValue = useMemo(() => ({
        ...dragState,
    }), [dragState]);

    return (
        <SarasDnDDragContext.Provider value={dragContextValue}>
            <DndContext
                autoScroll={autoScroll}
                collisionDetection={closestCenter}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDragStart={handleDragStart}
                sensors={sensors}
                {...restProps}
            >
                {children}
                <DragOverlay>
                    {activeItem ? (
                        <div style={activeItem.cardStyle}>
                            {activeItem.children}
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </SarasDnDDragContext.Provider>
    )
}

export default SarasDnDProvider;
